import * as React from "react";
import Box from "@amzn/awsui-components-react/polaris/box";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import StatusIndicator from "@amzn/awsui-components-react/polaris/status-indicator";
import CopyToClipboard from "@amzn/awsui-components-react/polaris/copy-to-clipboard";
import { Fill } from "./journey-types";
import { mapFillStatus, mapOrderStatus, mapShipmentStatus } from "./journey-status-utils";

interface FillTimelineProps {
  fill: Fill;
}

const STAGE_STYLES = {
  fill: {
    background: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
    borderColor: "#1976d2",
    iconColor: "#1976d2",
    emoji: "💊",
  },
  order: {
    background: "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)",
    borderColor: "#388e3c",
    iconColor: "#388e3c",
    emoji: "📦",
  },
  shipment: {
    background: "linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)",
    borderColor: "#f57c00",
    iconColor: "#f57c00",
    emoji: "🚚",
  },
};

function StageCard({ stage, title, children }: { stage: "fill" | "order" | "shipment"; title: string; children: React.ReactNode }) {
  const style = STAGE_STYLES[stage];
  return (
    <div style={{
      flex: 1,
      padding: "16px 20px",
      background: style.background,
      borderRadius: "12px",
      border: `2px solid ${style.borderColor}30`,
      minWidth: "200px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      transition: "transform 0.2s, box-shadow 0.2s",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
        <span style={{ fontSize: "18px" }}>{style.emoji}</span>
        <span style={{ fontWeight: 700, fontSize: "13px", color: style.iconColor, textTransform: "uppercase", letterSpacing: "0.5px" }}>
          {title}
        </span>
      </div>
      <div>{children}</div>
    </div>
  );
}

function ArrowConnector() {
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      padding: "0 4px",
    }}>
      <div style={{
        width: "40px",
        height: "3px",
        background: "linear-gradient(90deg, #90caf9, #66bb6a, #ffb74d)",
        borderRadius: "2px",
        position: "relative",
      }}>
        <div style={{
          position: "absolute",
          right: "-6px",
          top: "-4px",
          width: 0,
          height: 0,
          borderTop: "5px solid transparent",
          borderBottom: "5px solid transparent",
          borderLeft: "8px solid #ffb74d",
        }} />
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
      <span style={{ fontSize: "11px", color: "#5f6b7a", fontWeight: 500, minWidth: "60px" }}>{label}:</span>
      <span style={{ fontSize: "12px", color: "#16191f", fontFamily: "monospace", wordBreak: "break-all" }}>{value}</span>
      <CopyToClipboard
        copyButtonText=""
        copySuccessText="✓"
        copyErrorText="!"
        textToCopy={value}
        variant="icon"
      />
    </div>
  );
}

const FillTimeline: React.FC<FillTimelineProps> = ({ fill }) => {
  const fillStatus = mapFillStatus(fill.fillStatus);
  const order = fill.order;
  const shipment = order?.shipment ?? null;

  return (
    <div style={{
      display: "flex",
      alignItems: "stretch",
      padding: "16px",
      backgroundColor: "#fafbfc",
      borderRadius: "16px",
      border: "1px solid #e9ebed",
      boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
      gap: "4px",
    }}>
      {/* Fill Stage */}
      <StageCard stage="fill" title="Fill">
        <SpaceBetween size="xs">
          <StatusIndicator type={fillStatus.type}>{fillStatus.label}</StatusIndicator>
          <DetailRow label="Fill ID" value={fill.fillId} />
          <DetailRow label="Status" value={fill.fillStatus} />
        </SpaceBetween>
      </StageCard>

      <ArrowConnector />

      {/* Order Stage */}
      <StageCard stage="order" title="Order">
        {order && order.orderId ? (
          <SpaceBetween size="xs">
            <StatusIndicator type={mapOrderStatus(order.orderStatus || "").type}>
              {mapOrderStatus(order.orderStatus || "").label}
            </StatusIndicator>
            <DetailRow label="Order ID" value={order.orderId} />
            {order.purchaseId && <DetailRow label="Purchase" value={order.purchaseId} />}
            {order.orderDate && <DetailRow label="Date" value={order.orderDate} />}
          </SpaceBetween>
        ) : (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60px" }}>
            <Box color="text-body-secondary" fontSize="body-s">⏳ Awaiting order</Box>
          </div>
        )}
      </StageCard>

      <ArrowConnector />

      {/* Shipment Stage */}
      <StageCard stage="shipment" title="Shipment">
        {shipment ? (
          <SpaceBetween size="xs">
            <StatusIndicator type={mapShipmentStatus(shipment.shipmentCondition).type}>
              {mapShipmentStatus(shipment.shipmentCondition).label}
            </StatusIndicator>
            <DetailRow label="Ship ID" value={shipment.shipmentId} />
            <DetailRow label="Status" value={shipment.shipmentCondition} />
          </SpaceBetween>
        ) : (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60px" }}>
            <Box color="text-body-secondary" fontSize="body-s">⏳ Awaiting shipment</Box>
          </div>
        )}
      </StageCard>
    </div>
  );
};

export default FillTimeline;
